import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  ListItemButton,
  InputAdornment,
  Stack,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import StarIcon from '@mui/icons-material/Star';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import ShareIcon from '@mui/icons-material/Share';
import GroupIcon from '@mui/icons-material/Group';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setLocalStorageWithCleanup } from '../utils/cacheManager';

// Empty string for same-origin API calls (nginx proxies /api/)
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
const TICKER_CACHE_KEY = 'cachedTickers';
const ACTIVE_WATCHLIST_STORAGE_KEY = 'watchlists.active.v1';
const ACTIVE_WATCHLIST_SCOPE_STORAGE_KEY = 'watchlists.active.scope.v1';
const WATCHLIST_STATE_CACHE_PREFIX = 'watchlists.state.v1';
const MAX_SEARCH_RESULTS = 50;

const delay = (ms) => new Promise((resolve) => {
  window.setTimeout(resolve, ms);
});

const getWatchlistStateCacheKey = (identity) => {
  const normalizedIdentity = String(identity || '').trim().toLowerCase();
  return normalizedIdentity ? `${WATCHLIST_STATE_CACHE_PREFIX}:${normalizedIdentity}` : '';
};

const readWatchlistStateCache = (cacheKey) => {
  if (!cacheKey) return null;
  try {
    return safeParse(localStorage.getItem(cacheKey), null);
  } catch {
    return null;
  }
};

const writeWatchlistStateCache = (cacheKey, state) => {
  if (!cacheKey) return;
  try {
    setLocalStorageWithCleanup(cacheKey, JSON.stringify(state), { targetRemovals: 12 });
  } catch {
    // Ignore storage failures.
  }
};

// Pre-configured filter for performance - matches ticker or name, limits results
const tickerFilterOptions = createFilterOptions({
  limit: MAX_SEARCH_RESULTS,
  stringify: (option) => `${option?.ticker || ''} ${option?.name || ''}`,
});

const defaultWatchlists = [
  {
    id: 'default-watchlist-mag7',
    name: 'MAG7',
    tickers: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA'],
  },
  {
    id: 'default-watchlist-space',
    name: 'Space Exploration',
    tickers: ['RKLB', 'LUNR', 'ASTS', 'SPCE', 'IRDM', 'PL'],
  },
];

const safeParse = (rawValue, fallbackValue) => {
  try {
    return JSON.parse(rawValue);
  } catch {
    return fallbackValue;
  }
};

const Watchlist = ({
  onSelectTicker,
  onSelectWatchlistIndex,
  watchlistIndexSelected = false,
  selectedExchange = 'NASDAQ',
  selectedTicker = '',
  compact = false,
  onManageWatchlists,
  navigateOnSelect = true,
}) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const runtimeEnv = window.__RUNTIME_ENV__ || {};
  const authAudience = String(runtimeEnv.AUTH0_AUDIENCE || process.env.REACT_APP_AUTH0_AUDIENCE || '').trim();
  const userId = isAuthenticated && user?.sub ? user.sub : 'anonymous';
  const userEmail = isAuthenticated && user?.email ? String(user.email).trim().toLowerCase() : '';
  const userDisplayName = isAuthenticated && user?.name ? String(user.name).trim() : '';
  const [authToken, setAuthToken] = useState('');
  const watchlistStateCacheKey = useMemo(
    () => getWatchlistStateCacheKey(userEmail || userId),
    [userEmail, userId]
  );
  const cachedWatchlistState = useMemo(
    () => readWatchlistStateCache(watchlistStateCacheKey),
    [watchlistStateCacheKey]
  );

  const getApiAccessToken = useCallback(async () => {
    const authorizationParams = {
      scope: 'openid profile email',
      ...(authAudience ? { audience: authAudience } : {}),
    };

    return String((await getAccessTokenSilently({ authorizationParams })) || '');
  }, [authAudience, getAccessTokenSilently]);

  useEffect(() => {
    let active = true;

    const loadToken = async () => {
      if (!isAuthenticated) {
        if (active) setAuthToken('');
        return;
      }

      try {
        const token = await getApiAccessToken();
        if (active) {
          setAuthToken(String(token || ''));
        }
      } catch (error) {
        if (active) {
          setAuthToken('');
        }
      }
    };

    loadToken();

    return () => {
      active = false;
    };
  }, [isAuthenticated, getApiAccessToken]);

  const authHeaders = useMemo(() => {
    const headers = { 'X-User-Id': userId };
    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }
    if (userDisplayName) {
      headers['X-User-Name'] = userDisplayName;
    }
    if (authToken) {
      headers.Authorization = `Bearer ${authToken}`;
    }
    return headers;
  }, [userId, userEmail, userDisplayName, authToken]);

  const buildRequestHeaders = useCallback(async () => {
    const headers = {
      'X-User-Id': userId,
    };

    if (userEmail) {
      headers['X-User-Email'] = userEmail;
    }
    if (userDisplayName) {
      headers['X-User-Name'] = userDisplayName;
    }

    let token = authToken;
    if (!token && isAuthenticated) {
      try {
        token = await getApiAccessToken();
        if (token) {
          setAuthToken(token);
        }
      } catch (error) {
        token = '';
      }
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }, [authToken, getApiAccessToken, isAuthenticated, userDisplayName, userEmail, userId]);

  const [watchlists, setWatchlists] = useState(() => {
    const cachedWatchlists = Array.isArray(cachedWatchlistState?.watchlists) ? cachedWatchlistState.watchlists : [];
    return cachedWatchlists.length ? cachedWatchlists : defaultWatchlists;
  });
  const [sharedWatchlists, setSharedWatchlists] = useState(() => (
    Array.isArray(cachedWatchlistState?.sharedWatchlists) ? cachedWatchlistState.sharedWatchlists : []
  ));
  const [watchlistsLoading, setWatchlistsLoading] = useState(true);
  const [activeWatchlistScope, setActiveWatchlistScope] = useState(() => {
    if (cachedWatchlistState?.activeWatchlistScope === 'shared') {
      return 'shared';
    }
    const savedScope = localStorage.getItem(ACTIVE_WATCHLIST_SCOPE_STORAGE_KEY);
    return savedScope === 'shared' ? 'shared' : 'owned';
  });

  const [activeWatchlistId, setActiveWatchlistId] = useState(() => {
    return String(cachedWatchlistState?.activeWatchlistId || localStorage.getItem(ACTIVE_WATCHLIST_STORAGE_KEY) || '');
  });

  const [newWatchlistName, setNewWatchlistName] = useState('');
  const [newTicker, setNewTicker] = useState('');
  const [shareTargetUserId, setShareTargetUserId] = useState('');
  const [sharePermission, setSharePermission] = useState('view');
  const [activeWatchlistShares, setActiveWatchlistShares] = useState([]);
  const [sharesLoading, setSharesLoading] = useState(false);
  const [followingUsers, setFollowingUsers] = useState([]);
  const [followingLoading, setFollowingLoading] = useState(false);
  const [newFollowIdentifier, setNewFollowIdentifier] = useState('');
  const [comparisonSourceWatchlistId, setComparisonSourceWatchlistId] = useState('');
  const [comparisonTickers, setComparisonTickers] = useState([]);
  const [activeTickerFilter, setActiveTickerFilter] = useState('');
  const [sidebarTickerFilter, setSidebarTickerFilter] = useState('');
  const [tickerOptions, setTickerOptions] = useState([]);
  const [draggedTicker, setDraggedTicker] = useState('');
  const [dragOverTicker, setDragOverTicker] = useState('');

  const selectActiveWatchlist = useCallback((scope, id) => {
    setActiveWatchlistScope(scope);
    setActiveWatchlistId(String(id));
    localStorage.setItem(ACTIVE_WATCHLIST_SCOPE_STORAGE_KEY, scope);
    localStorage.setItem(ACTIVE_WATCHLIST_STORAGE_KEY, String(id));
  }, []);

  useEffect(() => {
    writeWatchlistStateCache(watchlistStateCacheKey, {
      watchlists,
      sharedWatchlists,
      activeWatchlistId,
      activeWatchlistScope,
      savedAt: Date.now(),
    });
  }, [activeWatchlistId, activeWatchlistScope, sharedWatchlists, watchlistStateCacheKey, watchlists]);

  // Fetch watchlists from API
  const loadWatchlists = useCallback(async () => {
    setWatchlistsLoading(true);
    if (!isAuthenticated) {
      setWatchlists(defaultWatchlists);
      setSharedWatchlists([]);
      setWatchlistsLoading(false);
      return;
    }
    try {
      let ownedRes = null;
      let sharedRes = null;
      let requestHeaders = null;

      for (let attempt = 0; attempt < 3; attempt += 1) {
        requestHeaders = await buildRequestHeaders();
        [ownedRes, sharedRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/watchlists`, {
            headers: requestHeaders,
          }),
          fetch(`${API_BASE_URL}/api/watchlists/shared`, {
            headers: requestHeaders,
          }),
        ]);

        if (ownedRes.ok && sharedRes.ok) {
          break;
        }

        const shouldRetry = [ownedRes, sharedRes].some((response) => response && [401, 403, 429, 500, 502, 503, 504].includes(response.status));
        if (!shouldRetry || attempt === 2) {
          break;
        }

        await delay(250 * (attempt + 1));
      }

      if (!ownedRes.ok) throw new Error(`HTTP ${ownedRes.status}`);
      if (!sharedRes.ok) throw new Error(`HTTP ${sharedRes.status}`);

      let ownedLists = await ownedRes.json();
      const sharedLists = await sharedRes.json();

      if (!Array.isArray(ownedLists)) {
        ownedLists = [];
      }

      setSharedWatchlists(Array.isArray(sharedLists) ? sharedLists : []);

      if (ownedLists.length > 0) {
        setWatchlists(ownedLists);
      } else {
        // No watchlists exist, create default presets.
        const defaultSeedLists = [
          { name: 'MAG7', tickers: ['AAPL', 'MSFT', 'GOOGL', 'AMZN', 'NVDA', 'META', 'TSLA'] },
          { name: 'Space Exploration', tickers: ['RKLB', 'LUNR', 'ASTS', 'SPCE', 'IRDM', 'PL'] },
        ];

        const createdLists = [];
        for (const seed of defaultSeedLists) {
          const createRes = await fetch(`${API_BASE_URL}/api/watchlists`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...requestHeaders },
            body: JSON.stringify(seed),
          });

          if (createRes.ok) {
            const newList = await createRes.json();
            createdLists.push(newList);
          }
        }

        if (createdLists.length > 0) {
          ownedLists = createdLists;
          setWatchlists(createdLists);
        }
      }

      const ownedExists = ownedLists.some((list) => String(list.id) === activeWatchlistId);
      const sharedExists = (Array.isArray(sharedLists) ? sharedLists : []).some(
        (list) => String(list.id) === activeWatchlistId
      );
      const currentStillValid =
        (activeWatchlistScope === 'owned' && ownedExists) ||
        (activeWatchlistScope === 'shared' && sharedExists);

      if (!currentStillValid) {
        if (ownedLists.length > 0) {
          selectActiveWatchlist('owned', ownedLists[0].id);
        } else if (Array.isArray(sharedLists) && sharedLists.length > 0) {
          selectActiveWatchlist('shared', sharedLists[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load watchlists:', err);
    } finally {
      setWatchlistsLoading(false);
    }
  }, [
    isAuthenticated,
    userId,
    activeWatchlistId,
    activeWatchlistScope,
    selectActiveWatchlist,
    buildRequestHeaders,
  ]);

  useEffect(() => {
    loadWatchlists();
  }, [loadWatchlists]);

  useEffect(() => {
    let cancelled = false;

    const loadTickerOptions = async () => {
      const cachedRaw = localStorage.getItem(TICKER_CACHE_KEY);
      const parsed = cachedRaw ? safeParse(cachedRaw, null) : null;
      const cachedData = parsed && Array.isArray(parsed.data) ? parsed.data : [];
      if (!cancelled && cachedData.length) {
        setTickerOptions(cachedData);
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/api/tickers`);
        const freshData = Array.isArray(response.data) ? response.data : [];

        if (!cancelled) {
          setTickerOptions(freshData);
        }

        localStorage.setItem(
          TICKER_CACHE_KEY,
          JSON.stringify({
            data: freshData,
            timestamp: Date.now(),
          })
        );
      } catch (error) {
        console.error('Error refreshing ticker metadata in Watchlist:', error);
      }
    };

    loadTickerOptions();

    return () => {
      cancelled = true;
    };
  }, []);

  // Create a lookup map from ticker symbol to company name
  const tickerNameMap = useMemo(() => {
    const map = {};
    tickerOptions.forEach((option) => {
      if (option?.ticker) {
        map[option.ticker] = option.name || option.ticker;
      }
    });
    return map;
  }, [tickerOptions]);

  const activeOwnedWatchlist = watchlists.find((list) => String(list.id) === activeWatchlistId);
  const activeSharedWatchlist = sharedWatchlists.find((list) => String(list.id) === activeWatchlistId);
  const allAccessibleWatchlists = useMemo(
    () => [
      ...watchlists.map((list) => ({ ...list, scope: 'owned' })),
      ...sharedWatchlists.map((list) => ({ ...list, scope: 'shared' })),
    ],
    [watchlists, sharedWatchlists]
  );

  const activeWatchlist =
    (activeWatchlistScope === 'shared' ? activeSharedWatchlist : activeOwnedWatchlist)
    || allAccessibleWatchlists[0]
    || watchlists[0];

  const isOwnedActiveWatchlist = activeWatchlist?.scope
    ? activeWatchlist.scope === 'owned'
    : activeWatchlistScope === 'owned';
  const canEditActiveWatchlist = Boolean(activeWatchlist)
    && (isOwnedActiveWatchlist || Boolean(activeWatchlist?.can_edit));

  const comparisonSourceWatchlist =
    allAccessibleWatchlists.find((list) => String(list.id) === comparisonSourceWatchlistId) || activeWatchlist;
  const filteredActiveTickers = useMemo(() => {
    const source = activeWatchlist?.tickers || [];
    const query = activeTickerFilter.trim().toUpperCase();
    if (!query) return source;
    return source.filter((ticker) => {
      const name = (tickerNameMap[ticker] || '').toUpperCase();
      return ticker.includes(query) || name.includes(query);
    });
  }, [activeWatchlist, activeTickerFilter, tickerNameMap]);

  const normalizedSelectedTicker = (selectedTicker || '').trim().toUpperCase();
  const compactTickers = useMemo(() => {
    const base = activeWatchlist?.tickers || [];
    const query = sidebarTickerFilter.trim().toUpperCase();
    if (!query) return base;
    return base.filter((ticker) => ticker.includes(query));
  }, [activeWatchlist, sidebarTickerFilter]);

  React.useEffect(() => {
    if (!comparisonSourceWatchlistId && activeWatchlist?.id) {
      setComparisonSourceWatchlistId(String(activeWatchlist.id));
    }
  }, [comparisonSourceWatchlistId, activeWatchlist]);

  React.useEffect(() => {
    if (!comparisonSourceWatchlist) {
      setComparisonTickers([]);
      return;
    }

    setComparisonTickers((prev) =>
      prev.filter((ticker) => comparisonSourceWatchlist.tickers.includes(ticker))
    );
  }, [comparisonSourceWatchlist]);

  const createWatchlist = async () => {
    const name = newWatchlistName.trim();
    if (!name) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/watchlists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ name, tickers: [] }),
      });
      if (!res.ok) throw new Error('Failed to create watchlist');
      const newList = await res.json();
      setWatchlists((prev) => [...prev, newList]);
      selectActiveWatchlist('owned', newList.id);
      setNewWatchlistName('');
    } catch (err) {
      console.error('Error creating watchlist:', err);
    }
  };

  const removeWatchlist = async (watchlistId) => {
    if (watchlists.length <= 1) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/watchlists/${watchlistId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      if (!res.ok) throw new Error('Failed to delete watchlist');
      
      const nextWatchlists = watchlists.filter((list) => String(list.id) !== String(watchlistId));
      const nextActiveId =
        activeWatchlistId === String(watchlistId) ? String(nextWatchlists[0].id) : activeWatchlistId;

      setWatchlists(nextWatchlists);
      selectActiveWatchlist('owned', nextActiveId);
    } catch (err) {
      console.error('Error deleting watchlist:', err);
    }
  };

  const loadActiveWatchlistShares = useCallback(async () => {
    if (!activeWatchlist?.id || !isOwnedActiveWatchlist) {
      setActiveWatchlistShares([]);
      return;
    }

    setSharesLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/watchlists/${activeWatchlist.id}/shares`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setActiveWatchlistShares(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading watchlist shares:', error);
      setActiveWatchlistShares([]);
    } finally {
      setSharesLoading(false);
    }
  }, [activeWatchlist?.id, isOwnedActiveWatchlist, authHeaders]);

  useEffect(() => {
    loadActiveWatchlistShares();
  }, [loadActiveWatchlistShares]);

  const loadFollowingUsers = useCallback(async () => {
    setFollowingLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/social/following`, {
        headers: authHeaders,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFollowingUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading following users:', error);
      setFollowingUsers([]);
    } finally {
      setFollowingLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadFollowingUsers();
  }, [loadFollowingUsers]);

  const followUser = async () => {
    const identifier = newFollowIdentifier.trim();
    if (!identifier) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/social/following`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ identifier }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to follow user');
      }

      setNewFollowIdentifier('');
      await loadFollowingUsers();
    } catch (error) {
      console.error('Error following user:', error);
      window.alert(error.message || 'Failed to follow user');
    }
  };

  const unfollowUser = async (identifier) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/social/following/${encodeURIComponent(identifier)}`, {
        method: 'DELETE',
        headers: authHeaders,
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to unfollow user');
      }

      await loadFollowingUsers();
    } catch (error) {
      console.error('Error unfollowing user:', error);
      window.alert(error.message || 'Failed to unfollow user');
    }
  };

  const followingShareOptions = useMemo(() => {
    const unique = new Set();
    followingUsers.forEach((entry) => {
      const userIdValue = String(entry?.user_id || '').trim();
      const emailValue = String(entry?.email || '').trim().toLowerCase();
      if (userIdValue) unique.add(userIdValue);
      if (emailValue) unique.add(emailValue);
    });
    return Array.from(unique);
  }, [followingUsers]);

  const shareActiveWatchlist = async () => {
    if (!activeWatchlist?.id || !isOwnedActiveWatchlist) return;

    const sharedWithUserId = shareTargetUserId.trim();
    if (!sharedWithUserId) return;

    try {
      const res = await fetch(`${API_BASE_URL}/api/watchlists/${activeWatchlist.id}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          sharedWithUserId,
          canEdit: sharePermission === 'edit',
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to share watchlist');
      }

      setShareTargetUserId('');
      await Promise.all([loadActiveWatchlistShares(), loadWatchlists()]);
    } catch (error) {
      console.error('Error sharing watchlist:', error);
      window.alert(error.message || 'Failed to share watchlist');
    }
  };

  const updateSharePermission = async (sharedWithUserId, canEdit) => {
    if (!activeWatchlist?.id || !isOwnedActiveWatchlist) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/watchlists/${activeWatchlist.id}/shares/${encodeURIComponent(sharedWithUserId)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ canEdit }),
        }
      );
      if (!res.ok) throw new Error('Failed to update permission');
      await Promise.all([loadActiveWatchlistShares(), loadWatchlists()]);
    } catch (error) {
      console.error('Error updating share permission:', error);
      window.alert('Failed to update permission');
    }
  };

  const revokeShare = async (sharedWithUserId) => {
    if (!activeWatchlist?.id || !isOwnedActiveWatchlist) return;

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/watchlists/${activeWatchlist.id}/shares/${encodeURIComponent(sharedWithUserId)}`,
        {
          method: 'DELETE',
          headers: authHeaders,
        }
      );
      if (!res.ok) throw new Error('Failed to remove share');
      await Promise.all([loadActiveWatchlistShares(), loadWatchlists()]);
    } catch (error) {
      console.error('Error deleting share:', error);
      window.alert('Failed to remove share');
    }
  };

  const persistActiveWatchlistTickers = useCallback(async (nextTickers) => {
    if (!activeWatchlist?.id) return null;

    const res = await fetch(`${API_BASE_URL}/api/watchlists/${activeWatchlist.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ tickers: nextTickers }),
    });
    if (!res.ok) {
      const errorPayload = await res.json().catch(() => ({}));
      throw new Error(errorPayload?.error || `Failed to update watchlist (HTTP ${res.status})`);
    }

    const updated = await res.json();

    setWatchlists((prev) =>
      prev.map((list) => (String(list.id) === String(activeWatchlist.id) ? updated : list))
    );
    setSharedWatchlists((prev) =>
      prev.map((list) => (String(list.id) === String(activeWatchlist.id) ? { ...list, ...updated } : list))
    );

    return updated;
  }, [activeWatchlist?.id, authHeaders]);

  const reorderTickers = useCallback((tickers, sourceTicker, targetTicker) => {
    const fromIndex = tickers.indexOf(sourceTicker);
    const toIndex = tickers.indexOf(targetTicker);

    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
      return tickers;
    }

    const next = [...tickers];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return next;
  }, []);

  const handleTickerDragStart = useCallback((ticker, event) => {
    if (!canEditActiveWatchlist) return;

    setDraggedTicker(ticker);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', ticker);
  }, [canEditActiveWatchlist]);

  const handleTickerDragOver = useCallback((ticker, event) => {
    if (!canEditActiveWatchlist || !draggedTicker || ticker === draggedTicker) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverTicker((prev) => (prev === ticker ? prev : ticker));
  }, [canEditActiveWatchlist, draggedTicker]);

  const handleTickerDragEnd = useCallback(() => {
    setDraggedTicker('');
    setDragOverTicker('');
  }, []);

  const handleTickerDrop = useCallback(async (targetTicker, event) => {
    event.preventDefault();

    if (!canEditActiveWatchlist || !activeWatchlist) {
      handleTickerDragEnd();
      return;
    }

    const sourceTicker = draggedTicker || event.dataTransfer.getData('text/plain');
    if (!sourceTicker || sourceTicker === targetTicker) {
      handleTickerDragEnd();
      return;
    }

    const baseTickers = Array.isArray(activeWatchlist.tickers) ? activeWatchlist.tickers : [];
    const nextTickers = reorderTickers(baseTickers, sourceTicker, targetTicker);

    handleTickerDragEnd();
    if (nextTickers === baseTickers) return;

    try {
      await persistActiveWatchlistTickers(nextTickers);
    } catch (error) {
      console.error('Error reordering tickers:', error);
    }
  }, [
    activeWatchlist,
    canEditActiveWatchlist,
    draggedTicker,
    handleTickerDragEnd,
    persistActiveWatchlistTickers,
    reorderTickers,
  ]);

  const addTickerToActiveList = async () => {
    if (!activeWatchlist) return;

    if (!canEditActiveWatchlist) {
      window.alert('You have view-only access to this watchlist.');
      return;
    }

    const ticker = (newTicker || '').trim().toUpperCase();
    if (!ticker) return;

    if (activeWatchlist.tickers.includes(ticker)) {
      setNewTicker('');
      return;
    }

    const newTickers = [...activeWatchlist.tickers, ticker];
    try {
      await persistActiveWatchlistTickers(newTickers);
      setNewTicker('');
    } catch (err) {
      console.error('Error adding ticker:', err);
      window.alert(err.message || 'Failed to add ticker to watchlist');
    }
  };

  const removeTickerFromActiveList = async (ticker) => {
    if (!activeWatchlist) return;

    if (!canEditActiveWatchlist) {
      window.alert('You have view-only access to this watchlist.');
      return;
    }

    const newTickers = activeWatchlist.tickers.filter((item) => item !== ticker);
    try {
      await persistActiveWatchlistTickers(newTickers);
    } catch (err) {
      console.error('Error removing ticker:', err);
      window.alert(err.message || 'Failed to remove ticker from watchlist');
    }
  };

  const openTickerDashboard = (ticker) => {
    if (onSelectTicker) {
      onSelectTicker(ticker, selectedExchange);
    }
    if (navigateOnSelect) {
      navigate('/dashboard');
    }
  };

  const openTickerForTrading = (ticker) => {
    if (onSelectTicker) {
      onSelectTicker(ticker, selectedExchange);
    }
    navigate('/paper-trading');
  };

  const openWatchlistIndex = () => {
    if (onSelectWatchlistIndex && activeWatchlist?.tickers?.length) {
      onSelectWatchlistIndex(activeWatchlist);
    }
    if (navigateOnSelect) {
      navigate('/dashboard');
    }
  };

  const openComparisonForSelectedTickers = () => {
    if (!comparisonTickers.length) return;

    navigate('/company-comparison', {
      state: {
        prefillTickers: comparisonTickers,
      },
    });
  };

  if (compact) {
    return (
      <Paper
        elevation={2}
        sx={{
          p: 1.5,
          position: 'sticky',
          top: 188,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          background: (theme) =>
            `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Watchlist
          </Typography>
          <Chip
            size="small"
            label={`${activeWatchlist?.tickers?.length || 0} tickers`}
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        </Box>

        <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
          <Select
            value={activeWatchlistId}
            onChange={(event) => {
              const nextId = String(event.target.value);
              const selected = allAccessibleWatchlists.find((list) => String(list.id) === nextId);
              selectActiveWatchlist(selected?.scope || 'owned', nextId);
            }}
          >
            {allAccessibleWatchlists.map((list) => (
              <MenuItem key={`sidebar-watchlist-${list.scope}-${list.id}`} value={String(list.id)}>
                {list.scope === 'shared' ? `${list.name} (Shared)` : list.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          fullWidth
          placeholder="Filter tickers"
          value={sidebarTickerFilter}
          onChange={(event) => setSidebarTickerFilter(event.target.value)}
          sx={{ mb: 1.5 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Add ticker"
            value={newTicker}
            disabled={!canEditActiveWatchlist}
            onChange={(event) => setNewTicker(event.target.value.toUpperCase())}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                addTickerToActiveList();
              }
            }}
          />
          <Tooltip title="Add ticker to this watchlist">
            <span>
              <IconButton
                color="primary"
                onClick={addTickerToActiveList}
                disabled={!newTicker.trim() || !activeWatchlist || !canEditActiveWatchlist}
                sx={{
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {compactTickers.length ? (
          <List
            dense
            sx={{
              maxHeight: '56vh',
              overflowY: 'auto',
              p: 0,
              borderRadius: 1,
              bgcolor: 'background.default',
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <ListItem key={`sidebar-${activeWatchlist?.id || 'active'}-index`} disablePadding>
              <ListItemButton
                selected={watchlistIndexSelected}
                onClick={openWatchlistIndex}
                sx={{
                  py: 0.4,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    '& .MuiListItemText-secondary': { color: 'primary.contrastText' },
                  },
                }}
              >
                <ListItemText
                  primary="Watchlist Index"
                  secondary={(activeWatchlist?.tickers?.length || 0) > 0
                    ? `${activeWatchlist?.tickers?.length || 0} constituents`
                    : 'No constituents'}
                  primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem' }}
                  secondaryTypographyProps={{ fontSize: '0.72rem', noWrap: true }}
                />
              </ListItemButton>
            </ListItem>
            {compactTickers.map((ticker) => (
              <ListItem key={`sidebar-${activeWatchlist.id}-${ticker}`} disablePadding>
                <Box
                  draggable={canEditActiveWatchlist}
                  onDragStart={(event) => handleTickerDragStart(ticker, event)}
                  onDragOver={(event) => handleTickerDragOver(ticker, event)}
                  onDrop={(event) => handleTickerDrop(ticker, event)}
                  onDragEnd={handleTickerDragEnd}
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                    width: '100%',
                    cursor: canEditActiveWatchlist ? 'grab' : 'default',
                    borderTop: dragOverTicker === ticker ? (theme) => `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                  }}
                >
                  <ListItemButton
                    selected={ticker === normalizedSelectedTicker}
                    onClick={() => openTickerDashboard(ticker)}
                    sx={{
                      py: 0.4,
                      pr: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'primary.contrastText',
                        '& .MuiListItemText-secondary': { color: 'primary.contrastText' },
                      },
                    }}
                  >
                    <ListItemText
                      primary={ticker}
                      secondary={tickerNameMap[ticker] && tickerNameMap[ticker] !== ticker ? tickerNameMap[ticker] : null}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                      secondaryTypographyProps={{ fontSize: '0.72rem', noWrap: true }}
                    />
                  </ListItemButton>
                  {canEditActiveWatchlist && (
                    <Tooltip title="Drag to reorder">
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: 0.25,
                          color: 'text.disabled',
                        }}
                      >
                        <DragIndicatorIcon fontSize="small" />
                      </Box>
                    </Tooltip>
                  )}
                  <Tooltip title={`Trade ${ticker}`}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        openTickerForTrading(ticker);
                      }}
                      sx={{
                        alignSelf: 'center',
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      <AccountBalanceWalletIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={`Remove ${ticker}`}>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeTickerFromActiveList(ticker);
                      }}
                      disabled={!canEditActiveWatchlist}
                      sx={{
                        alignSelf: 'center',
                        mr: 0.5,
                        color: 'text.secondary',
                        '&:hover': { color: 'error.main' },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {activeWatchlist?.tickers?.length ? 'No tickers match your filter.' : 'No tickers in this list.'}
          </Typography>
        )}

        <Button
          fullWidth
          size="small"
          variant="outlined"
          onClick={onManageWatchlists || (() => navigate('/watchlist'))}
          sx={{ mt: 1.5, fontWeight: 600 }}
        >
          Manage Watchlists
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2.5,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
          background: (theme) =>
            `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.action.hover} 100%)`,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon color="primary" />
              Watchlists
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Curate investment lists, open dashboards faster, and compare selected companies.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Chip label={`${watchlists.length} list${watchlists.length === 1 ? '' : 's'}`} variant="outlined" />
            <Chip
              label={`${activeWatchlist?.tickers?.length || 0} active ticker${(activeWatchlist?.tickers?.length || 0) === 1 ? '' : 's'}`}
              color="primary"
              variant="outlined"
            />
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              height: '100%',
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1.25, fontWeight: 700 }}>
              Your Lists
            </Typography>

            <Box display="flex" gap={1} mb={1.75}>
              <TextField
                fullWidth
                size="small"
                label="New list name"
                value={newWatchlistName}
                onChange={(e) => setNewWatchlistName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    createWatchlist();
                  }
                }}
              />
              <Button variant="contained" onClick={createWatchlist} disabled={!newWatchlistName.trim()}>
                <AddIcon fontSize="small" />
              </Button>
            </Box>

            <Divider sx={{ mb: 1.25 }} />

            {watchlistsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <List dense sx={{ p: 0 }}>
                {watchlists.map((list) => {
                  const isSelected = String(list.id) === activeWatchlistId && activeWatchlistScope === 'owned';
                  return (
                    <ListItem
                      key={list.id}
                      disablePadding
                      secondaryAction={
                        <Tooltip title={watchlists.length <= 1 ? 'At least one list is required' : 'Delete list'}>
                          <span>
                            <IconButton
                              edge="end"
                              size="small"
                              onClick={() => removeWatchlist(list.id)}
                              disabled={watchlists.length <= 1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      }
                      sx={{ mb: 0.75 }}
                    >
                      <ListItemButton
                        onClick={() => {
                          selectActiveWatchlist('owned', list.id);
                        }}
                        selected={isSelected}
                        sx={{
                          borderRadius: 1.5,
                          pr: 5,
                          border: (theme) => `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                          '&.Mui-selected': {
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            '& .MuiListItemText-secondary': { color: 'primary.contrastText', opacity: 0.92 },
                          },
                        }}
                      >
                        <ListItemText
                          primary={list.name}
                          secondary={`${list.tickers.length} ticker${list.tickers.length === 1 ? '' : 's'}`}
                          primaryTypographyProps={{ fontWeight: 700, fontSize: '0.95rem' }}
                          secondaryTypographyProps={{ fontSize: '0.77rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}

            {!!sharedWatchlists.length && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <GroupIcon fontSize="small" />
                  Shared With Me
                </Typography>
                <List dense sx={{ p: 0 }}>
                  {sharedWatchlists.map((list) => {
                    const isSelected = String(list.id) === activeWatchlistId && activeWatchlistScope === 'shared';
                    return (
                      <ListItem key={`shared-${list.id}`} disablePadding sx={{ mb: 0.75 }}>
                        <ListItemButton
                          onClick={() => selectActiveWatchlist('shared', list.id)}
                          selected={isSelected}
                          sx={{
                            borderRadius: 1.5,
                            border: (theme) => `1px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
                            '&.Mui-selected': {
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                            },
                          }}
                        >
                          <ListItemText
                            primary={list.name}
                            secondary={`${list.tickers.length} ticker${list.tickers.length === 1 ? '' : 's'} • ${list.can_edit ? 'Can edit' : 'View only'}`}
                            primaryTypographyProps={{ fontWeight: 700, fontSize: '0.9rem' }}
                            secondaryTypographyProps={{ fontSize: '0.76rem' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              sx={{ mb: 1.5 }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {activeWatchlist?.name || 'Active Watchlist'}
              </Typography>
              <Chip
                size="small"
                label={`${activeWatchlist?.tickers?.length || 0} holdings${canEditActiveWatchlist ? '' : ' • View only'}`}
                variant="outlined"
                color="primary"
              />
            </Stack>

            <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: 'action.hover', mb: 2 }}>
              <Box display="flex" gap={1} mb={1.25}>
                <Autocomplete
                  freeSolo
                  options={tickerOptions}
                  value={newTicker}
                  disabled={!canEditActiveWatchlist}
                  onInputChange={(event, value) => setNewTicker((value || '').toUpperCase())}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option?.ticker || '';
                  }}
                  filterOptions={tickerFilterOptions}
                  renderOption={(props, option) => {
                    const { key, ...restProps } = props;
                    return (
                      <li key={option?.ticker || option} {...restProps}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {option?.ticker || option}
                          </Typography>
                          {option?.name && option.name !== option.ticker && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.76rem' }}>
                              {option.name}
                            </Typography>
                          )}
                        </Box>
                      </li>
                    );
                  }}
                  isOptionEqualToValue={(option, value) => {
                    const optionTicker = typeof option === 'string' ? option : option?.ticker;
                    const valueTicker = typeof value === 'string' ? value : value?.ticker;
                    return optionTicker === valueTicker;
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Add ticker"
                      size="small"
                      placeholder="e.g. NVDA"
                    />
                  )}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  onClick={addTickerToActiveList}
                  disabled={!newTicker.trim() || !canEditActiveWatchlist}
                >
                  Add
                </Button>
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="Filter holdings by ticker or company name"
                value={activeTickerFilter}
                onChange={(event) => setActiveTickerFilter(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Divider sx={{ mb: 2 }} />

            {activeWatchlist?.tickers?.length ? (
              filteredActiveTickers.length ? (
                <Box display="flex" flexWrap="wrap" gap={1.1}>
                  {filteredActiveTickers.map((ticker) => (
                    <Tooltip
                      key={`${activeWatchlist.id}-${ticker}`}
                      title={tickerNameMap[ticker] || ticker}
                      arrow
                      placement="top"
                    >
                      <Chip
                        label={
                          tickerNameMap[ticker] && tickerNameMap[ticker] !== ticker
                            ? `${ticker} — ${tickerNameMap[ticker]}`
                            : ticker
                        }
                        onDelete={canEditActiveWatchlist ? () => removeTickerFromActiveList(ticker) : undefined}
                        deleteIcon={<DeleteIcon />}
                        color="primary"
                        variant="outlined"
                        icon={<VisibilityIcon />}
                        onClick={() => openTickerDashboard(ticker)}
                        sx={{
                          maxWidth: 320,
                          borderRadius: 1.25,
                          '& .MuiChip-label': { fontWeight: 600 },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No holdings match your filter.
                </Typography>
              )
            ) : (
              <Typography variant="body2" color="text.secondary">
                This watchlist is empty. Add a ticker to get started.
              </Typography>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.75 }}>
              Tip: click a ticker chip to open its dashboard. Use the delete icon to remove it from the list.
            </Typography>

            {isOwnedActiveWatchlist && (
              <>
                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <ShareIcon fontSize="small" />
                  Share Watchlist
                </Typography>

                <Grid container spacing={1.2} alignItems="center" sx={{ mb: 1.25 }}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      freeSolo
                      options={followingShareOptions}
                      value={shareTargetUserId}
                      onInputChange={(event, value) => setShareTargetUserId(value || '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          size="small"
                          label="Share with"
                          placeholder="Follower, user ID, or email"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={sharePermission}
                        onChange={(event) => setSharePermission(event.target.value)}
                      >
                        <MenuItem value="view">View only</MenuItem>
                        <MenuItem value="edit">Can edit</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={shareActiveWatchlist}
                      disabled={!shareTargetUserId.trim()}
                    >
                      Share
                    </Button>
                  </Grid>
                </Grid>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Follow users once, then share watchlists to them quickly from the dropdown.
                </Typography>

                <Grid container spacing={1.2} alignItems="center" sx={{ mb: 1.25 }}>
                  <Grid item xs={12} md={9}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Follow user"
                      placeholder="auth0|... or user@email.com"
                      value={newFollowIdentifier}
                      onChange={(event) => setNewFollowIdentifier(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          followUser();
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={followUser}
                      disabled={!newFollowIdentifier.trim() || followingLoading}
                    >
                      Follow
                    </Button>
                  </Grid>
                </Grid>

                {followingUsers.length > 0 ? (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
                    {followingUsers.map((followEntry) => {
                      const followId = followEntry?.user_id;
                      const followEmail = followEntry?.email;
                      const label = followEmail && followEmail !== followId
                        ? `${followEmail}`
                        : `${followId}`;
                      return (
                        <Chip
                          key={`following-${followId}`}
                          label={label}
                          variant="outlined"
                          onClick={() => setShareTargetUserId(followEmail || followId)}
                          onDelete={() => unfollowUser(followId)}
                          size="small"
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                    {followingLoading ? 'Loading followed users...' : 'You are not following anyone yet.'}
                  </Typography>
                )}

                {sharesLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" color="text.secondary">
                      Loading current shares...
                    </Typography>
                  </Box>
                ) : activeWatchlistShares.length ? (
                  <List dense sx={{ p: 0, mb: 1.25 }}>
                    {activeWatchlistShares.map((share) => (
                      <ListItem
                        key={`${activeWatchlist.id}-${share.shared_with_user_id}`}
                        sx={{
                          px: 0,
                          py: 0.5,
                          display: 'flex',
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: 1.25,
                          flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        }}
                      >
                        <ListItemText
                          primary={share.shared_with_user_id}
                          secondary={share.can_edit ? 'Can edit' : 'View only'}
                          primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
                          secondaryTypographyProps={{ fontSize: '0.76rem' }}
                          sx={{ mr: 1 }}
                        />

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            ml: { xs: 0, sm: 'auto' },
                            mt: { xs: 0.5, sm: 0 },
                            width: { xs: '100%', sm: 'auto' },
                            justifyContent: { xs: 'space-between', sm: 'flex-end' },
                            flexShrink: 0,
                          }}
                        >
                          <FormControl size="small" sx={{ minWidth: 150, width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: 240, sm: 'none' } }}>
                            <Select
                              value={share.can_edit ? 'edit' : 'view'}
                              onChange={(event) =>
                                updateSharePermission(
                                  share.shared_with_user_id,
                                  event.target.value === 'edit'
                                )
                              }
                            >
                              <MenuItem value="view">View only</MenuItem>
                              <MenuItem value="edit">Can edit</MenuItem>
                            </Select>
                          </FormControl>

                          <Button
                            size="small"
                            color="error"
                            onClick={() => revokeShare(share.shared_with_user_id)}
                            sx={{ minWidth: 'auto', px: 1.25, alignSelf: { xs: 'stretch', sm: 'auto' } }}
                          >
                            Remove
                          </Button>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                    This watchlist has not been shared yet.
                  </Typography>
                )}
              </>
            )}

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Compare Companies
            </Typography>

            <Grid container spacing={1.2} alignItems="center">
              <Grid item xs={12} md={4}>
                <FormControl size="small" fullWidth>
                  <Select
                    value={comparisonSourceWatchlistId}
                    onChange={(event) => setComparisonSourceWatchlistId(String(event.target.value))}
                    displayEmpty
                  >
                    {allAccessibleWatchlists.map((list) => (
                      <MenuItem key={`compare-source-${list.scope}-${list.id}`} value={String(list.id)}>
                        {list.scope === 'shared' ? `${list.name} (Shared)` : list.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={5}>
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  options={comparisonSourceWatchlist?.tickers || []}
                  value={comparisonTickers}
                  onChange={(event, value) => setComparisonTickers(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Tickers to compare"
                      placeholder="Select 2+ tickers"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={openComparisonForSelectedTickers}
                  disabled={comparisonTickers.length < 2}
                >
                  Compare Selected
                </Button>
              </Grid>
            </Grid>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Opens Company Comparison prefilled with your selected tickers.
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Watchlist;
